import { create } from 'zustand';
import { loadPersisted, savePersisted, type NavEntry } from '@/features/tabs/tabsPersistence';

// Tree state is now multi-tab: each tab carries its own active container,
// active folder, and folder-expansion set. The top-level fields
// (activeContainerId / activeFolderId / expanded) are a façade mirroring the
// active tab, so existing call sites keep working unchanged.

export interface Tab {
  id: string;
  containerId: string | null;
  folderId: string | null;
  expanded: Set<string>;
  // 'tree' = the folder tree; 'detail' = the folder-detail page (breadcrumb +
  // sortable mail table) for detailFolderId. Persisted per-tab, so reopening the
  // pane returns to the exact location the user left instead of the tree.
  mode: 'tree' | 'detail';
  detailFolderId: string | null;
  // Browser-style navigation history for this tab: the folder-detail locations
  // visited, with a cursor. `goBack`/`goForward` move the cursor; opening a
  // folder truncates the forward entries and pushes. `historyIndex` is -1 when
  // no detail page has been opened yet. Persisted so back/forward survive a
  // reopen. (The tree/home is reached via the Home button, not via history.)
  history: NavEntry[];
  historyIndex: number;
}

interface ClosedTabSnapshot {
  containerId: string | null;
  folderId: string | null;
  expanded: string[];
  closedAt: number;
}

interface TreeState {
  tabs: Tab[];
  activeTabId: string;
  recentlyClosed: ClosedTabSnapshot[];

  // façade — these always reflect the active tab.
  activeContainerId: string | null;
  activeFolderId: string | null;
  expanded: Set<string>;
  mode: 'tree' | 'detail';
  detailFolderId: string | null;
  // Whether the active tab can navigate back/forward through its detail history.
  canGoBack: boolean;
  canGoForward: boolean;

  setActiveContainer: (id: string | null) => void;
  setActiveFolder: (id: string | null) => void;
  toggleExpanded: (id: string) => void;
  expand: (id: string) => void;
  // Open the folder-detail page for a folder (null = the container's root, i.e.
  // its top-level folders). backToTree returns to the tree view.
  openFolderDetail: (containerId: string, folderId: string | null) => void;
  backToTree: () => void;
  // Browser-style back/forward through the visited folder-detail pages.
  goBack: () => void;
  goForward: () => void;

  openTab: (params: {
    containerId: string | null;
    folderId: string | null;
    expanded?: Iterable<string>;
    activate?: boolean;
    insertAfter?: string;
  }) => string;
  // Open a NEW tab already in folder-detail mode at (containerId,
  // detailFolderId) — detailFolderId null = the container root. Backs the
  // "Ouvrir dans un nouvel onglet" action on a folder (detail view) and on a
  // project row (home). Seeds the tab's history with that single location so
  // back/forward behave as if the user navigated there directly.
  openDetailTab: (containerId: string, detailFolderId: string | null) => string;
  closeTab: (tabId: string) => void;
  activateTab: (tabId: string) => void;
  duplicateTab: (tabId: string) => string | null;
  reopenLastClosed: () => string | null;
  moveTab: (activeId: string, overId: string) => void;
}

const MAX_RECENTLY_CLOSED = 10;

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyTab(): Tab {
  return {
    id: newId(),
    containerId: null,
    folderId: null,
    expanded: new Set(),
    mode: 'tree',
    detailFolderId: null,
    history: [],
    historyIndex: -1,
  };
}

function mirror(tabs: Tab[], activeTabId: string) {
  const active = tabs.find((t) => t.id === activeTabId) ?? tabs[0]!;
  return {
    activeContainerId: active.containerId,
    activeFolderId: active.folderId,
    expanded: active.expanded,
    mode: active.mode,
    detailFolderId: active.detailFolderId,
    canGoBack: active.historyIndex > 0,
    canGoForward: active.historyIndex >= 0 && active.historyIndex < active.history.length - 1,
  };
}

// Enter a folder-detail page and record it in the tab's history: drop any
// forward entries, then push — unless we're already on that exact location
// (re-opening the current folder mustn't create a duplicate or a dead forward
// step). Container is carried per-entry so back/forward restore it too.
function withDetailNavigation(
  tab: Tab,
  containerId: string,
  detailFolderId: string | null,
): Tab {
  const base = { ...tab, containerId, mode: 'detail' as const, detailFolderId };
  const current = tab.history[tab.historyIndex];
  if (
    current &&
    current.containerId === containerId &&
    current.mode === 'detail' &&
    current.detailFolderId === detailFolderId
  ) {
    return base;
  }
  const entry: NavEntry = { containerId, mode: 'detail', detailFolderId };
  const history = [...tab.history.slice(0, tab.historyIndex + 1), entry];
  return { ...base, history, historyIndex: history.length - 1 };
}

// Les onglets persistés sont réhydratés SANS contrôle d'accès, et c'est
// volontaire : on ne purge PAS les onglets dont l'espace est absent de la liste
// des containers. « Absent de la liste » ne prouve pas l'inaccessibilité — cette
// liste exclut les espaces archivés, qui restent pourtant accessibles et
// remontent même dans la recherche : purger sur ce critère détruirait l'onglet
// d'un utilisateur en train de travailler sur un projet archivé. Et un onglet
// pointant vers un espace réellement devenu inaccessible se signale tout seul
// (ses requêtes répondent 403), ce qui est très préférable à la destruction
// silencieuse et irréversible d'un onglet.
function hydrate(): { tabs: Tab[]; activeTabId: string } {
  const persisted = loadPersisted();
  if (persisted && persisted.tabs.length > 0) {
    return {
      tabs: persisted.tabs.map((t) => {
        // Backward-compatible defaults: tabs written by the older shape have no
        // mode/detailFolderId/history — treat them as a tree view with no
        // history rather than dropping the tab.
        const mode = t.mode ?? 'tree';
        const detailFolderId = t.detailFolderId ?? null;
        const history = Array.isArray(t.history) ? t.history : [];
        const historyIndex =
          typeof t.historyIndex === 'number'
            ? Math.max(-1, Math.min(t.historyIndex, history.length - 1))
            : history.length - 1;
        return {
          id: t.id,
          containerId: t.containerId,
          folderId: t.folderId,
          expanded: new Set(t.expanded),
          mode,
          detailFolderId,
          history,
          historyIndex,
        };
      }),
      activeTabId: persisted.activeTabId,
    };
  }
  const t = emptyTab();
  return { tabs: [t], activeTabId: t.id };
}

const initial = hydrate();

export const useTreeStore = create<TreeState>((set, get) => {
  const updateActiveTab = (mut: (tab: Tab) => Tab) => {
    set((s) => {
      const tabs = s.tabs.map((t) => (t.id === s.activeTabId ? mut(t) : t));
      return { tabs, ...mirror(tabs, s.activeTabId) };
    });
  };

  return {
    tabs: initial.tabs,
    activeTabId: initial.activeTabId,
    recentlyClosed: [],
    ...mirror(initial.tabs, initial.activeTabId),

    setActiveContainer: (id) =>
      updateActiveTab((t) => ({ ...t, containerId: id, folderId: null })),

    setActiveFolder: (id) => updateActiveTab((t) => ({ ...t, folderId: id })),

    toggleExpanded: (id) =>
      updateActiveTab((t) => {
        const next = new Set(t.expanded);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return { ...t, expanded: next };
      }),

    // Idempotent open — used to reveal a folder after a mail is filed into it
    // so the freshly filed mail becomes visible without a manual click.
    expand: (id) =>
      updateActiveTab((t) => {
        if (t.expanded.has(id)) return t;
        const next = new Set(t.expanded);
        next.add(id);
        return { ...t, expanded: next };
      }),

    openFolderDetail: (containerId, folderId) =>
      updateActiveTab((t) => withDetailNavigation(t, containerId, folderId)),

    backToTree: () => updateActiveTab((t) => ({ ...t, mode: 'tree' })),

    goBack: () =>
      updateActiveTab((t) => {
        if (t.historyIndex <= 0) return t;
        const idx = t.historyIndex - 1;
        const e = t.history[idx]!;
        return { ...t, containerId: e.containerId, mode: 'detail', detailFolderId: e.detailFolderId, historyIndex: idx };
      }),

    goForward: () =>
      updateActiveTab((t) => {
        if (t.historyIndex < 0 || t.historyIndex >= t.history.length - 1) return t;
        const idx = t.historyIndex + 1;
        const e = t.history[idx]!;
        return { ...t, containerId: e.containerId, mode: 'detail', detailFolderId: e.detailFolderId, historyIndex: idx };
      }),

    openTab: ({ containerId, folderId, expanded, activate = true, insertAfter }) => {
      const id = newId();
      const tab: Tab = {
        id,
        containerId,
        folderId,
        expanded: new Set(expanded ?? []),
        mode: 'tree',
        detailFolderId: null,
        history: [],
        historyIndex: -1,
      };
      set((s) => {
        let tabs: Tab[];
        if (insertAfter) {
          const idx = s.tabs.findIndex((t) => t.id === insertAfter);
          tabs = idx === -1 ? [...s.tabs, tab] : [
            ...s.tabs.slice(0, idx + 1),
            tab,
            ...s.tabs.slice(idx + 1),
          ];
        } else {
          tabs = [...s.tabs, tab];
        }
        const activeTabId = activate ? id : s.activeTabId;
        return { tabs, activeTabId, ...mirror(tabs, activeTabId) };
      });
      return id;
    },

    openDetailTab: (containerId, detailFolderId) => {
      const id = newId();
      const entry: NavEntry = { containerId, mode: 'detail', detailFolderId };
      const tab: Tab = {
        id,
        containerId,
        folderId: null,
        expanded: new Set(),
        mode: 'detail',
        detailFolderId,
        history: [entry],
        historyIndex: 0,
      };
      set((s) => {
        const tabs = [...s.tabs, tab];
        return { tabs, activeTabId: id, ...mirror(tabs, id) };
      });
      return id;
    },

    closeTab: (tabId) => {
      set((s) => {
        const idx = s.tabs.findIndex((t) => t.id === tabId);
        if (idx === -1) return s;
        const closing = s.tabs[idx]!;
        // Snapshot the closed tab so it can be re-opened.
        const snapshot: ClosedTabSnapshot = {
          containerId: closing.containerId,
          folderId: closing.folderId,
          expanded: Array.from(closing.expanded),
          closedAt: Date.now(),
        };
        const recentlyClosed = [snapshot, ...s.recentlyClosed].slice(
          0,
          MAX_RECENTLY_CLOSED,
        );
        let tabs = s.tabs.filter((t) => t.id !== tabId);
        let activeTabId = s.activeTabId;
        if (tabs.length === 0) {
          const fresh = emptyTab();
          tabs = [fresh];
          activeTabId = fresh.id;
        } else if (tabId === s.activeTabId) {
          const fallback = tabs[Math.min(idx, tabs.length - 1)]!;
          activeTabId = fallback.id;
        }
        return { tabs, activeTabId, recentlyClosed, ...mirror(tabs, activeTabId) };
      });
    },

    activateTab: (tabId) => {
      set((s) => {
        if (!s.tabs.some((t) => t.id === tabId)) return s;
        return { activeTabId: tabId, ...mirror(s.tabs, tabId) };
      });
    },

    duplicateTab: (tabId) => {
      const state = get();
      const src = state.tabs.find((t) => t.id === tabId);
      if (!src) return null;
      return state.openTab({
        containerId: src.containerId,
        folderId: src.folderId,
        expanded: src.expanded,
        activate: true,
        insertAfter: tabId,
      });
    },

    reopenLastClosed: () => {
      const state = get();
      const head = state.recentlyClosed[0];
      if (!head) return null;
      set((s) => ({ recentlyClosed: s.recentlyClosed.slice(1) }));
      return state.openTab({
        containerId: head.containerId,
        folderId: head.folderId,
        expanded: head.expanded,
        activate: true,
      });
    },

    moveTab: (activeId, overId) => {
      if (activeId === overId) return;
      set((s) => {
        const from = s.tabs.findIndex((t) => t.id === activeId);
        const to = s.tabs.findIndex((t) => t.id === overId);
        if (from === -1 || to === -1) return s;
        const tabs = s.tabs.slice();
        const [moved] = tabs.splice(from, 1);
        tabs.splice(to, 0, moved!);
        return { tabs, ...mirror(tabs, s.activeTabId) };
      });
    },
  };
});

// Persist any state change. The subscribe callback fires after every set().
useTreeStore.subscribe((s) => {
  savePersisted({
    tabs: s.tabs.map((t) => ({
      id: t.id,
      containerId: t.containerId,
      folderId: t.folderId,
      expanded: Array.from(t.expanded),
      // Persist the open location + history so reopening the pane lands the user
      // back where they were (and keeps back/forward working).
      mode: t.mode,
      detailFolderId: t.detailFolderId,
      history: t.history,
      historyIndex: t.historyIndex,
    })),
    activeTabId: s.activeTabId,
  });
});
